// ─── BrandLogo.jsx — CashHorizon brand mark (animated, letterless) ─────────
// Renders the same visual as the in-app AI-chat icon: a gradient rounded
// square with two counter-rotating semi-square border rings spinning
// around it. Never draws anything inside the square — that's the brand rule.
//
// The CSS pattern mirrors `.ai-icon` / `.ai-icon-ring` in landing/index.html
// (lines 83-95) so the identity is consistent across surfaces.

import React from "react";

// Inject the keyframes once, at module load. Same animation name shared by
// every instance — no per-render work, no collisions.
if (typeof document !== "undefined" && !document.querySelector("style[data-ch-brand-logo]")) {
  const s = document.createElement("style");
  s.setAttribute("data-ch-brand-logo", "");
  s.textContent = "@keyframes chBrandLogoSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}

export default function BrandLogo({ size = 36, style = null, className = "" }) {
  // Proportions match .ai-icon.size-nav in landing/index.html: wrapper 36,
  // inner 32, ring1 inset -1, ring2 inset -4. Scale linearly for other sizes.
  const s = size;
  const innerSize    = Math.round(s * 32 / 36);
  const innerRadius  = Math.round(s * 9  / 36);
  const ring1Inset   = Math.round(s * -1 / 36);
  const ring1Radius  = Math.round(s * 11 / 36);
  const ring2Inset   = Math.round(s * -4 / 36);
  const ring2Radius  = Math.round(s * 14 / 36);

  return (
    <div
      role="img"
      aria-label="CashHorizon"
      className={className}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: s,
        height: s,
        ...(style || {}),
      }}
    >
      {/* Inner gradient square — empty by brand rule, never letters or glyphs */}
      <div style={{
        width: innerSize,
        height: innerSize,
        borderRadius: innerRadius,
        background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
        boxShadow: "0 4px 16px rgba(139,92,246,0.4)",
        position: "relative",
        zIndex: 2,
      }} />
      {/* Ring 1 — top + right arcs, 1.2 s forward */}
      <div style={{
        position: "absolute",
        inset: ring1Inset,
        borderRadius: ring1Radius,
        border: "2px solid transparent",
        borderTopColor: "#8b5cf6",
        borderRightColor: "#06b6d4",
        animation: "chBrandLogoSpin 1.2s linear infinite",
        zIndex: 1,
        willChange: "transform",
        backfaceVisibility: "hidden",
      }} />
      {/* Ring 2 — bottom + left arcs, 2.4 s reverse, lower opacity */}
      <div style={{
        position: "absolute",
        inset: ring2Inset,
        borderRadius: ring2Radius,
        border: "1.5px solid transparent",
        borderBottomColor: "#8b5cf6",
        borderLeftColor: "#06b6d4",
        opacity: 0.35,
        animation: "chBrandLogoSpin 2.4s linear infinite reverse",
        willChange: "transform",
        backfaceVisibility: "hidden",
      }} />
    </div>
  );
}
